/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import type {
  AssetSummary,
  Calendar,
  Memory,
  ResearchConversation,
  ThreadConversation,
  Transcript
} from '@repo/common';
import { getDateStr, getLocalDate } from '@repo/toolslib/src/utils/date.util';
import type { Writable } from 'svelte/store';
import { derived, writable } from 'svelte/store';
import { setupRequired } from './onboarding.store';

export interface Viewer extends Record<string, any> {
  activeType?:
    | 'account'
    | 'execute'
    | 'calendar'
    | 'event'
    | 'recording'
    | 'config'
    | 'task'
    | 'goal'
    | 'home'
    | 'search'
    | 'memory'
    | 'drive'
    | 'about'
    | 'research'
    | 'drafts'
    | 'thread'
    | 'start'
    | 'taskplan';
  folder?: 'inbox' | 'all';
  configTab: 'api' | 'devices';
  slideOpen: boolean;
  calendarModalOpen: boolean;
  onboardingStep: string;

  searchOpen: boolean;
  newItemModalOpen: boolean;
  importModalOpen: boolean;
  active?: Memory | Calendar | Event | ResearchConversation | ThreadConversation | AssetSummary;
  activeTranscript?: Transcript;
  currentDate: string;
  popoverCalendarEvent?: Memory;
  providerModalOpen: boolean;
  modalTitle: string;
  trayPopover: boolean;
  noteTab: 'event' | 'text' | 'notes' | 'summary' | 'transcription' | 'threads';
  threadTab: 'summary' | 'other';
  memoriesOpen: boolean;
}

export const viewer = writable<Viewer>({
  configTab: 'api',
  folder: 'all',
  activeType: 'home',
  timeGrain: 'week',
  active: undefined,
  slideOpen: false,
  calendarModalOpen: false,
  onboardingStep: '1',
  hasSideChat: false,
  searchOpen: false,
  newItemModalOpen: false,
  importModalOpen: false,
  currentDate: getDateStr(getLocalDate()),
  popoverCalendarEvent: undefined,
  providerModalOpen: false,
  modalTitle: '',
  trayPopover: false,
  noteTab: 'notes',
  threadTab: 'summary',
  memoriesOpen: false
});

// const createViewer = () => {
//     const initialValue: Viewer = {
//         activeType: 'calendar',
//         timeGrain: 'week',
//         active: undefined,
//         configTab: "api",
//         currentDate: getDateStr(new Date())
//     };
//     const internalStore = writable<Viewer>(initialValue);
//     const {subscribe, update, set} = internalStore;
//
//     return {
//         update,
//         subscribe,
//         set,
//         updateCurrentDate: (date: Date) => {
//             update((v) => {
//                 v.currentDate = getDateStr(date);
//                 return v;
//             });
//         },
//     }
// }
//
// export const viewer = createViewer();

export const active = derived(viewer, ($d) => {
  if (!$d.active) return null;
  return $d.active;
});

export const showQueryInput = derived(
  [viewer, setupRequired],
  ([$viewer, $setupRequired]): boolean => {
    return (
      ['memory', 'drive', 'calendar', 'research'].includes($viewer.activeType || '') &&
      !$viewer.searchOpen &&
      !$viewer.providerModalOpen &&
      !$viewer.calendarModalOpen &&
      !$setupRequired
    );
  }
);

viewer.subscribe(($viewer) => {
  // only subscribe on the client side
  const isServer = typeof window === 'undefined';
  if (isServer) return;
  if (!$viewer.searchOpen) {
    const body = document.querySelector('body');
    if (body) {
      body.style.pointerEvents = 'auto';
      body.style.overflow = '';
    }
  }
});

export const contentPaneScrollable: Writable<HTMLElement | null> = writable(null);

// export const viewerCurrentDate = derived(viewer, ($d) => {
// 	if ($d.currentDate) {
// 		return fromDateStr($d.currentDate);
// 	}
// });
